import { useState } from "react";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import NowAiring from "@/components/NowAiring";
import { Reveal } from "@/components/Reveal";
import { WEEKDAY_SLOTS, WEEKEND_SLOTS, SHOWS_BY_SLUG, isWeekend, getNowAiring } from "@/data/content";
import { Info } from "lucide-react";

export default function Schedule() {
    const [tab, setTab] = useState(isWeekend() ? "weekend" : "weekday");
    const slots = tab === "weekday" ? WEEKDAY_SLOTS : WEEKEND_SLOTS;
    const now = getNowAiring();
    const activeSlot = (tab === "weekend") === isWeekend() ? now.current : null;

    return (
        <div data-testid="schedule-page">
            <PageHero
                kicker="Programme Schedule"
                title="Plan your"
                accent="viewing"
                sub="Here's what's on Rongeen TV — weekdays and weekends, from sunrise lifestyle to prime-time entertainment."
                crumb="Schedule"
            />

            <div className="bg-[#0F0F23] pt-4">
                <NowAiring />
            </div>

            <section className="bg-[#0F0F23] py-16 sm:py-24">
                <div className="mx-auto max-w-5xl px-4 sm:px-6">
                    <div className="inline-flex rounded-full border border-white/10 bg-[#16162B] p-1.5" data-testid="schedule-tabs">
                        {[
                            { id: "weekday", label: "Monday – Friday" },
                            { id: "weekend", label: "Saturday – Sunday" },
                        ].map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                data-testid={`schedule-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`relative rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                                    tab === t.id ? "text-white" : "text-[#9999BB] hover:text-white"
                                }`}
                            >
                                {tab === t.id && (
                                    <motion.span
                                        layoutId="schedule-pill"
                                        className="absolute inset-0 rounded-full bg-[#C8102E]"
                                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10">{t.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="mt-10 space-y-3" data-testid="schedule-list">
                        {slots.map((slot, i) => {
                            const show = SHOWS_BY_SLUG[slot.show];
                            const live = activeSlot && activeSlot.show === slot.show;
                            return (
                                <Reveal key={`${tab}-${slot.show}`} delay={i * 0.04}>
                                    <div
                                        data-testid={`schedule-row-${slot.show}`}
                                        className={`group flex items-center gap-4 rounded-2xl border p-4 transition-all duration-300 sm:gap-6 sm:p-5 ${
                                            live
                                                ? "border-[#C8102E] bg-[#C8102E]/10 shadow-lg shadow-[#C8102E]/20"
                                                : "border-white/10 bg-[#16162B] hover:border-[#C8102E]/40"
                                        }`}
                                    >
                                        <span className="font-mono2 w-20 shrink-0 text-sm font-bold text-[#FFA200] sm:text-base">{slot.t}</span>
                                        <img
                                            src={show.img}
                                            alt={show.title}
                                            loading="lazy"
                                            className="hidden h-14 w-24 shrink-0 rounded-lg border border-white/10 object-cover sm:block"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="font-display truncate text-base font-bold text-white sm:text-lg">{show.title}</p>
                                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">{show.category}</p>
                                        </div>
                                        {live && (
                                            <span data-testid="schedule-live-badge" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#C8102E] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                                                <span className="onair-dot h-2 w-2 rounded-full bg-white" /> On Air
                                            </span>
                                        )}
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.15}>
                        <p className="mt-10 flex items-start gap-3 rounded-2xl border border-white/10 bg-[#16162B] p-5 text-xs leading-relaxed text-[#9999BB]">
                            <Info size={16} className="mt-0.5 shrink-0 text-[#FFA200]" />
                            Schedule is subject to change. Special programming may be aired during festivals and events.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
